import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import Cookies from '../../common/Cookies';
import { API_URL } from '../../common/Constants';
import { useReseller } from '../../contexts/ResellerContext';
import MsTeamsCredentialsModal from '../integrations/MsTeamsCredentialsModal';
import SlackCredentialsModal from '../integrations/SlackCredentialsModal';
import WhatsAppCredentialsModal from '../integrations/WhatsAppCredentialsModal';
import EmailCredentialsModal from '../integrations/EmailCredentialsModal';
import MSTeamsLogo from '../../assets/tools-logos/tools-msteams.png';

const cardStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: 12,
  padding: 32,
  width: 260,
  margin: 16,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  background: '#fff',
};

const cardsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  marginTop: 16,
};

const SlackLogo = () => (
  <Box
    sx={{
      width: 64,
      height: 64,
      mb: 2,
      borderRadius: 2,
      background: '#4A154B',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 700,
      fontSize: 28,
      fontFamily: 'sans-serif'
    }}
    aria-label="Slack logo"
  >
    #
  </Box>
);

const WhatsAppLogo = () => (
  <Box
    sx={{
      width: 64,
      height: 64,
      mb: 2,
      borderRadius: 2,
      background: '#25D366',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 700,
      fontSize: 32,
      fontFamily: 'sans-serif'
    }}
    aria-label="WhatsApp logo"
  >
    W
  </Box>
);

const EmailLogo = () => (
  <Box
    sx={{
      width: 64,
      height: 64,
      mb: 2,
      borderRadius: 2,
      background: '#1565C0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 700,
      fontSize: 28,
      fontFamily: 'sans-serif'
    }}
    aria-label="Email logo"
  >
    @
  </Box>
);

export default function ChannelIntegrations({ agentId, tenantId }) {
  const { reseller } = useReseller();
  const productName = reseller?.productName || 'Chattitude';
  const [msTeamsConnected, setMsTeamsConnected] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msTeamsModalOpen, setMsTeamsModalOpen] = useState(false);
  const [slackModalOpen, setSlackModalOpen] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const authHeaders = () => {
    const jwtToken = Cookies.getJwtToken();
    const tenant = Cookies.getTenantId();
    return jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});
  };

  useEffect(() => {
    const checkCredentials = async () => {
      if (!tenantId || !agentId) {
        setMsTeamsConnected(false);
        setSlackConnected(false);
        setWhatsappConnected(false);
        setEmailConnected(false);
        return;
      }
      setLoading(true);
      const headers = authHeaders();
      try {
        await axios.get(`${API_URL}/agent-ms-teams-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        setMsTeamsConnected(true);
      } catch (err) {
        setMsTeamsConnected(false);
      }
      try {
        await axios.get(`${API_URL}/agent-slack-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        setSlackConnected(true);
      } catch (err) {
        setSlackConnected(false);
      }
      try {
        await axios.get(`${API_URL}/agent-whatsapp-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        setWhatsappConnected(true);
      } catch (err) {
        setWhatsappConnected(false);
      }
      try {
        await axios.get(`${API_URL}/agent-email-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        setEmailConnected(true);
      } catch (err) {
        setEmailConnected(false);
      } finally {
        setLoading(false);
      }
    };
    checkCredentials();
  }, [tenantId, agentId]);

  const connectedBadge = (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 12px',
        marginBottom: 12,
        borderRadius: 4,
        backgroundColor: '#4caf50',
        color: '#fff',
        fontWeight: 600,
        fontSize: 12
      }}
    >
      Connected
    </span>
  );

  const settingsButton = (onClick) => (
    <button
      onClick={onClick}
      style={{
        padding: '8px 24px',
        fontSize: 16,
        borderRadius: 6,
        border: 'none',
        background: 'var(--primary-color, #1976d2)',
        color: '#fff',
        cursor: 'pointer'
      }}
    >
      Settings
    </button>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Channel Integrations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect your agent to messaging channels. Configure credentials for Teams, Slack, Email, and more.
      </Typography>
      <div style={cardsContainerStyle}>
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 12 }}>MS Teams</div>
          <img
            src={MSTeamsLogo}
            alt="MS Teams logo"
            style={{ width: 64, height: 64, marginBottom: 16, objectFit: 'contain' }}
          />
          <div style={{ fontSize: 15, color: '#555', textAlign: 'center', marginBottom: 20, minHeight: 48 }}>
            Connect your MS Teams bot to enable chat via Teams and Web Chat.
          </div>
          {msTeamsConnected && connectedBadge}
          {settingsButton(() => setMsTeamsModalOpen(true))}
        </div>

        <div style={cardStyle}>
          <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 12 }}>Slack</div>
          <SlackLogo />
          <div style={{ fontSize: 15, color: '#555', textAlign: 'center', marginBottom: 20, minHeight: 48 }}>
            Connect a Slack app so users can chat with this agent via Slack DMs.
          </div>
          {slackConnected && connectedBadge}
          {settingsButton(() => setSlackModalOpen(true))}
        </div>
{/* 
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 12 }}>WhatsApp</div>
          <WhatsAppLogo />
          <div style={{ fontSize: 15, color: '#555', textAlign: 'center', marginBottom: 20, minHeight: 48 }}>
            Connect OpenWA so users can chat with this agent via WhatsApp DMs.
          </div>
          {whatsappConnected && connectedBadge}
          {settingsButton(() => setWhatsappModalOpen(true))}
        </div> */}

        <div style={cardStyle}>
          <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 12 }}>Email</div>
          <EmailLogo />
          <div style={{ fontSize: 15, color: '#555', textAlign: 'center', marginBottom: 20, minHeight: 48 }}>
            Connect a support inbox. Unseen emails are answered by this agent with threaded replies.
          </div>
          {emailConnected && connectedBadge}
          {settingsButton(() => setEmailModalOpen(true))}
        </div>
      </div>

      <MsTeamsCredentialsModal
        open={msTeamsModalOpen}
        onClose={() => setMsTeamsModalOpen(false)}
        onCredentialsSaved={() => setMsTeamsConnected(true)}
        tenantId={tenantId}
        agentId={agentId}
      />

      <SlackCredentialsModal
        open={slackModalOpen}
        onClose={() => setSlackModalOpen(false)}
        onCredentialsSaved={() => setSlackConnected(true)}
        tenantId={tenantId}
        agentId={agentId}
        productName={productName}
        resellerBaseUrl={reseller?.baseUrl}
      />

      {/* <WhatsAppCredentialsModal
        open={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        onCredentialsSaved={() => setWhatsappConnected(true)}
        tenantId={tenantId}
        agentId={agentId}
        productName={productName}
        resellerBaseUrl={reseller?.baseUrl}
      /> */}

      <EmailCredentialsModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onCredentialsSaved={() => setEmailConnected(true)}
        tenantId={tenantId}
        agentId={agentId}
        productName={productName}
      />
    </Box>
  );
}
